import { messageWithIntent } from '@mysten/sui/cryptography';
import { TransactionDataBuilder } from '@mysten/sui/transactions';
import { fromBase64, normalizeStructTag, SUI_TYPE_ARG, toBase64 } from '@mysten/sui/utils';

import {
  createTransactionIntent,
  submitTransactionIntent,
  TransactionGasMode,
  type TransactionIntent,
  TransactionIntentType,
  type TransactionSubmission,
  TransactionSubmissionStatus,
} from '@/api/wallet/queries';
import { config } from '@/config';

import { PRIVY_API_URL } from '../use-privy-wallet';
import { assertSendTransaction, SendAssertionError } from './assert-send-tx';
import { getCoinDecimals } from './coin-decimals';
import { getOwnedCoinObjects, type OwnedCoinObjectsResult } from './owned-coin-objects';

export interface SendInput {
  coinType: string;
  decimals: number;
  amount: bigint;
  recipient: string;
}

interface VerifiedIntent {
  intentId: string;
  walletId: string;
  transactionBytesBase64: string;
  gasMode: TransactionGasMode | undefined;
}

type AuthorizationRequest = ReturnType<typeof authorizationRequestFor>;

interface TransferSigner {
  generateAuthorizationSignature: (request: AuthorizationRequest) => Promise<{ signature: string }>;
  refreshWalletSession: () => Promise<unknown>;
  assertCurrent: () => void;
}

export const PENDING_SUBMISSION: TransactionSubmission = {
  submissionId: '',
  status: TransactionSubmissionStatus.SUBMITTING,
};

const SPONSORED_GAS_COINS: OwnedCoinObjectsResult = {
  coins: [],
  addressSeen: true,
  addressBalance: '0',
};

export async function createVerifiedIntent(input: SendInput): Promise<VerifiedIntent> {
  await assertNetworkDecimals(input);
  const intent = await createTransactionIntent({
    type: TransactionIntentType.COIN_TRANSFER,
    parameters: {
      coinType: input.coinType,
      amount: input.amount.toString(),
      recipient: input.recipient,
    },
    idempotencyKey: crypto.randomUUID(),
  });
  return verifyIntentBytes(intent);
}

export async function buildAuthorizationRequest(
  intent: VerifiedIntent,
  input: SendInput,
  senderAddress: string,
): Promise<AuthorizationRequest> {
  const { inputCoins, gasCoins } = await readTransferCoins(intent, input, senderAddress);
  assertSendTransaction({
    txBytes: intent.transactionBytesBase64,
    expected: {
      sender: senderAddress,
      recipient: input.recipient,
      coinType: input.coinType,
      amount: input.amount,
      ownedInputObjectIds: inputCoins.coins.map((coin) => coin.objectId),
      ownedGasObjectIds: gasCoins.coins.map((coin) => coin.objectId),
      chainIdentifier: inputCoins.chainIdentifier,
    },
  });
  return authorizationRequestFor(intent);
}

export async function signAuthorizationRequest(
  request: AuthorizationRequest,
  signer: TransferSigner,
): Promise<string> {
  try {
    return (await signer.generateAuthorizationSignature(request)).signature;
  } catch (error) {
    if (!isRetryablePrivyAuthorizationError(error)) {
      throw error;
    }

    await signer.refreshWalletSession();
    signer.assertCurrent();
    return (await signer.generateAuthorizationSignature(request)).signature;
  }
}

export async function submitOrAssumePending(
  intentId: string,
  authorizationSignature: string,
): Promise<TransactionSubmission> {
  try {
    return await submitTransactionIntent(intentId, { authorizationSignature });
  } catch {
    return PENDING_SUBMISSION;
  }
}

async function assertNetworkDecimals(input: SendInput): Promise<void> {
  // From our own chain connection, never Gamma's API — see assert-send-tx.
  const chainDecimals = await getCoinDecimals(input.coinType);

  if (chainDecimals === null) {
    throw new Error('Could not verify the coin right now. Please try again shortly.');
  }

  if (chainDecimals !== input.decimals) {
    throw new SendAssertionError('the coin decimals do not match the network');
  }
}

function verifyIntentBytes(intent: TransactionIntent): VerifiedIntent {
  const { intentId, walletId, transactionBytesBase64, expectedHash } = intent;

  if (!walletId || !transactionBytesBase64) {
    throw new Error('The transaction is not ready to authorize. Please try again.');
  }

  const digest = TransactionDataBuilder.getDigestFromBytes(fromBase64(transactionBytesBase64));

  if (!expectedHash || digest !== expectedHash) {
    throw new SendAssertionError('the transaction digest does not match the intent');
  }

  return { intentId, walletId, transactionBytesBase64, gasMode: intent.gas?.mode };
}

async function readOwnedCoins(
  senderAddress: string,
  coinType: string,
  label: 'coins' | 'gas coins',
): Promise<OwnedCoinObjectsResult> {
  const ownership = await getOwnedCoinObjects(senderAddress, coinType);

  if (!ownership.addressSeen) {
    throw new Error(`Could not verify your ${label} right now. Please try again shortly.`);
  }

  return ownership;
}

async function readTransferCoins(
  intent: VerifiedIntent,
  input: SendInput,
  senderAddress: string,
): Promise<{ inputCoins: OwnedCoinObjectsResult; gasCoins: OwnedCoinObjectsResult }> {
  const inputCoins = await readOwnedCoins(senderAddress, input.coinType, 'coins');

  if (intent.gasMode !== TransactionGasMode.SELF_PAID) {
    return { inputCoins, gasCoins: SPONSORED_GAS_COINS };
  }

  if (normalizeStructTag(input.coinType) === normalizeStructTag(SUI_TYPE_ARG)) {
    return { inputCoins, gasCoins: inputCoins };
  }

  const gasCoins = await readOwnedCoins(senderAddress, SUI_TYPE_ARG, 'gas coins');
  return { inputCoins, gasCoins };
}

function authorizationRequestFor(intent: VerifiedIntent) {
  const intentMessage = messageWithIntent(
    'TransactionData',
    fromBase64(intent.transactionBytesBase64),
  );
  return {
    version: 1,
    method: 'POST',
    url: `${PRIVY_API_URL}/wallets/${intent.walletId}/raw_sign`,
    body: {
      params: {
        bytes: toBase64(intentMessage),
        encoding: 'base64',
        hash_function: 'blake2b256',
      },
    },
    headers: { 'privy-app-id': config.privy.appId },
  } as const;
}

function isRetryablePrivyAuthorizationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const privyError = error as { message?: unknown; privyErrorCode?: unknown };
  const message = typeof privyError.message === 'string' ? privyError.message.toLowerCase() : '';
  return (
    privyError.privyErrorCode === 'must_be_authenticated' ||
    message.includes('must be authenticated') ||
    message.includes('wallet proxy not initialized')
  );
}
